import { useState } from 'react';
import { createPortal } from 'react-dom';
import { post, refresh } from '../api';
import { QrCode } from '../components/camera';
import { Err, Tag, errMsg, useApp } from '../components/ui';

const ShelfLabel = ({ code, name }: { code: string; name: string }) => (
  <div className="lbl">
    <div className="qr">
      <QrCode text={code} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div className="lno">{code}</div>
      <div className="lname">{name}</div>
    </div>
  </div>
);
const TextLabel = ({ no }: { no: string }) => <div className="tlbl">{no}</div>;

export function Labels() {
  const { d, toast } = useApp();
  const [f, setF] = useState<'shelves' | 'items'>('shelves');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState<string[] | null>(null);
  const list: [string, string][] = f === 'shelves' ? d.b.shelves.map((s) => [s.code, s.name]) : d.b.items.filter((i) => i.status !== 'gone').map((i) => [i.no, '']);
  const toggle = (c: string) => {
    const n = new Set(picked);
    if (n.has(c)) n.delete(c);
    else n.add(c);
    setPicked(n);
  };
  const print = () => {
    const c = list.filter(([code]) => picked.has(code)).map(([code]) => code);
    if (!c.length) return toast('Pick at least one label');
    setPrinting(c);
    document.body.classList.add('printing');
    setTimeout(() => {
      window.print();
      document.body.classList.remove('printing');
      setPrinting(null);
    }, 400);
  };
  return (
    <>
      <h1>Labels</h1>
      <p className="sub">Shelves get a QR sticker. Items get a text label with the number only, made with the label maker. The label stays on the item for its whole life.</p>
      <div className="row" style={{ marginBottom: 12 }}>
        {(
          [
            ['shelves', 'Shelf QR stickers'],
            ['items', 'Item text labels'],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            className={`btn small ${f === k ? 'on' : ''}`}
            onClick={() => {
              setF(k);
              setPicked(new Set());
            }}
          >
            {l}
          </button>
        ))}
        <button className="btn small" onClick={() => setPicked(new Set(list.map(([c]) => c)))}>
          Select all
        </button>
        <button className="btn primary small" style={{ marginLeft: 'auto' }} onClick={print}>
          Print selected
        </button>
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))' }}>
        {list.map(([code, name]) => (
          <label key={code} className="pick">
            <input type="checkbox" checked={picked.has(code)} onChange={() => toggle(code)} />
            {f === 'shelves' ? <ShelfLabel code={code} name={name} /> : <TextLabel no={code} />}
          </label>
        ))}
      </div>
      {printing &&
        createPortal(
          <div id="printArea">{printing.map((c) => (f === 'shelves' ? <ShelfLabel key={c} code={c} name={d.shelf(c)?.name ?? ''} /> : <TextLabel key={c} no={c} />))}</div>,
          document.body,
        )}
    </>
  );
}

const EXAMPLE = `number\tarticle name\tcategory\tproject\tquantity\tshelf place\tstatus\tnote
PH-26-07-015\tSoldering station\t07\t\t1\tW1-1-L\ton shelf\t
PH-26-03-0322-004\tSyslogic edge processor v2\t03\t0322\t1\tS1-3-R\ton shelf\tfor Istanbul
PH-26-06-004\tCable lugs M6\t06\t\t200\tS2-1-M\ton shelf\tbox`;

interface Row {
  line: number;
  no: string;
  art: string;
  newArt: boolean;
  place: string;
  status: string;
  errs: string[];
  warns: string[];
}

export function Import() {
  const { d, toast } = useApp();
  const [text, setText] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const ok = rows?.filter((r) => !r.errs.length).length ?? 0;
  const check = async () => {
    setErr('');
    setBusy(true);
    try {
      setRows(await post<Row[]>('/api/import/check', { text }));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    setErr('');
    setBusy(true);
    try {
      const r = await post<{ imported: number }>('/api/import/apply', { text });
      await refresh();
      toast(`${r.imported} rows imported. New numbers continue from the highest in each group.`);
      setRows(null);
      setText('');
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <h1>Import sheet</h1>
      <p className="sub">Labeling starts with a shared sheet, one row per number. Paste the sheet here. The app checks every row against the numbering rules first. After import, only the app gives numbers and it continues from the highest number in each group.</p>
      <div className="panel">
        <p className="meta" style={{ margin: '0 0 6px' }}>
          Columns, in this order:
        </p>
        <p className="code" style={{ margin: '0 0 10px', fontSize: 15 }}>
          number · article name · category · project · quantity · shelf place · status · note
        </p>
        <p className="meta" style={{ margin: '0 0 10px' }}>
          Copy the rows from the sheet (with or without the header row) and paste them below. Status can be: on shelf, taken out, in repair, lost, thrown away. Shelf place looks like S1-2-L.
        </p>
        <textarea style={{ minHeight: 150, fontFamily: 'ui-monospace,monospace', fontSize: 13 }} value={text} onChange={(e) => setText(e.target.value)} placeholder={EXAMPLE.split('\n')[1]} />
        <div className="row" style={{ marginTop: 10 }}>
          <button className="btn primary" disabled={busy || !text.trim()} onClick={() => void check()}>
            Check rows
          </button>
          <button
            className="btn"
            onClick={() => {
              setText(EXAMPLE);
              setRows(null);
            }}
          >
            Load example rows
          </button>
        </div>
      </div>
      <Err e={err} />
      {rows && (
        <>
          <h2>
            Check result: {ok} OK, {rows.length - ok} with problems
          </h2>
          <div className="panel scroll" style={{ padding: '4px 8px' }}>
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Number</th>
                  <th>Article</th>
                  <th>Shelf place</th>
                  <th>Status</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.line}>
                    <td>{r.line}</td>
                    <td className="code">{r.no}</td>
                    <td>
                      {r.art}
                      {r.newArt && (
                        <>
                          {' '}
                          <Tag cls="soft">new article</Tag>
                        </>
                      )}
                    </td>
                    <td className="code">{r.place}</td>
                    <td>{r.status}</td>
                    <td>
                      {r.errs.length ? (
                        r.errs.map((e) => (
                          <span key={e} className="tag bad" style={{ whiteSpace: 'normal', marginRight: 4 }}>
                            {e}
                          </span>
                        ))
                      ) : (
                        <Tag cls="ok">OK</Tag>
                      )}
                      {r.warns.map((e) => (
                        <span key={e} className="tag warn" style={{ whiteSpace: 'normal', marginLeft: 4 }}>
                          {e}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            {d.isOwner ? (
              <>
                <button className="btn primary" disabled={!ok || busy} onClick={() => void apply()}>
                  Import the {ok} OK rows
                </button>
                <span className="meta">Rows with problems are skipped. Fix them in the sheet and import again.</span>
              </>
            ) : (
              <span className="meta">Only the warehouse owner can import.</span>
            )}
          </div>
        </>
      )}
    </>
  );
}
